'use client'

import { apiFetch } from '@/lib/api-fetch'

export type AiSquarePromptSourceId = 'banana-prompt-quicker' | 'awesome-gpt-image-2-prompts'
export type AiSquarePromptMode = 'generate' | 'edit'

export interface AiSquarePromptItem {
  id: string
  title: string
  previewUrl: string
  thumbnailUrl: string
  referenceImageUrls: string[]
  prompt: string
  author: string
  sourceUrl?: string
  mode: AiSquarePromptMode
  category: string
  subCategory?: string
  created?: string
  source: AiSquarePromptSourceId
  sourceLabel: string
  isNsfw: boolean
}

export interface AiSquarePromptMarketPayload {
  items: AiSquarePromptItem[]
  categories: string[]
  updatedAt: string
  totalSourceCount?: number
  readyThumbnailCount?: number
  pendingThumbnailCount?: number
}

export const AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_KEY = 'ai-square:prompt-market:v20260601'
export const AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_TTL_MS = 6 * 60 * 60 * 1000

let aiSquarePromptMarketPrewarmPromise: Promise<AiSquarePromptMarketPayload | null> | null = null
const aiSquarePrewarmedImageUrls = new Set<string>()

export function isAiSquarePromptMarketPayload(value: unknown): value is AiSquarePromptMarketPayload {
  if (!value || typeof value !== 'object') return false
  const payload = value as Partial<AiSquarePromptMarketPayload>
  return Array.isArray(payload.items)
    && Array.isArray(payload.categories)
    && typeof payload.updatedAt === 'string'
}

export function readCachedAiSquarePromptMarket(): AiSquarePromptMarketPayload | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_KEY)
    if (!raw) return null

    const cached = JSON.parse(raw) as { savedAt?: unknown; payload?: unknown }
    const savedAt = typeof cached.savedAt === 'number' ? cached.savedAt : 0
    if (!savedAt || Date.now() - savedAt > AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_TTL_MS) {
      window.localStorage.removeItem(AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_KEY)
      return null
    }
    if (!isAiSquarePromptMarketPayload(cached.payload)) return null
    return cached.payload
  } catch {
    return null
  }
}

export function writeCachedAiSquarePromptMarket(payload: AiSquarePromptMarketPayload): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      AI_SQUARE_PROMPT_MARKET_CLIENT_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), payload }),
    )
  } catch {
    // localStorage quota or privacy mode should not block the market page.
  }
}

function prewarmAiSquareThumbnailUrls(payload: AiSquarePromptMarketPayload, limit: number) {
  if (typeof window === 'undefined') return
  payload.items
    .filter((item) => !item.isNsfw && typeof item.thumbnailUrl === 'string' && item.thumbnailUrl)
    .slice(0, limit)
    .forEach((item) => {
      if (aiSquarePrewarmedImageUrls.has(item.thumbnailUrl)) return
      aiSquarePrewarmedImageUrls.add(item.thumbnailUrl)
      const image = new window.Image()
      image.decoding = 'async'
      image.loading = 'eager'
      image.src = item.thumbnailUrl
    })
}

export async function prewarmAiSquarePromptMarketClient(options: { thumbnailLimit?: number } = {}) {
  const cachedPayload = readCachedAiSquarePromptMarket()
  if (cachedPayload) {
    prewarmAiSquareThumbnailUrls(cachedPayload, options.thumbnailLimit ?? 8)
    return cachedPayload
  }
  if (aiSquarePromptMarketPrewarmPromise) return aiSquarePromptMarketPrewarmPromise

  aiSquarePromptMarketPrewarmPromise = (async () => {
    try {
      const response = await apiFetch('/api/ai-square/prompts')
      if (!response.ok) return null
      const payload = await response.json() as AiSquarePromptMarketPayload
      if (!isAiSquarePromptMarketPayload(payload)) return null
      writeCachedAiSquarePromptMarket(payload)
      prewarmAiSquareThumbnailUrls(payload, options.thumbnailLimit ?? 8)
      return payload
    } catch {
      return null
    } finally {
      aiSquarePromptMarketPrewarmPromise = null
    }
  })()

  return aiSquarePromptMarketPrewarmPromise
}
